import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Building2, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import {
    getSettlementsPaginated,
    deleteSettlement,
    loadToCostCenter,
    getLaundries,
} from '../Services/payroll.services';
import ColillaModal from './ColillaModal';

const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value || 0);

// Formatea la fecha en UTC (las fechas del período se guardan a medianoche UTC,
// así evitamos el corrimiento de día por zona horaria).
const fmtDate = (value) => {
    if (!value) return '—';
    const iso = typeof value === 'string' ? value : new Date(value).toISOString();
    const [y, m, d] = iso.substring(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

const SavedSettlements = ({ employeeId, refreshKey }) => {
    const { toast } = useToast();
    const [settlements, setSettlements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [laundries, setLaundries] = useState([]);
    const [loadDialog, setLoadDialog] = useState(null); // settlement seleccionado
    const [selectedCentro, setSelectedCentro] = useState('');
    const [processing, setProcessing] = useState(false);
    const [colillaSettlement, setColillaSettlement] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getSettlementsPaginated({
                page: 1,
                limit: 50,
                filters: employeeId ? { empleadoId: employeeId } : {},
            });
            setSettlements(res?.data || []);
        } catch (error) {
            console.error('Error fetching settlements:', error);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshKey]);

    const openLoadDialog = async (settlement) => {
        setLoadDialog(settlement);
        setSelectedCentro(settlement.lavanderia?.id || '');
        if (laundries.length === 0) {
            try {
                setLaundries(await getLaundries());
            } catch (error) {
                console.error('Error fetching laundries:', error);
            }
        }
    };

    const handleLoadToCostCenter = async () => {
        if (!loadDialog) return;
        try {
            setProcessing(true);
            const centro = laundries.find((l) => l._id === selectedCentro);
            const centros = centro
                ? [{
                    id: centro._id,
                    nombre: centro.nombre,
                    costo: loadDialog.valores?.totalNeto ?? loadDialog.valores?.totalAPagar ?? 0,
                }]
                : undefined;
            const res = await loadToCostCenter(loadDialog._id, { nivel: 'centro', centros });
            if (res?.state) {
                toast({ title: 'Éxito', description: 'Liquidación cargada al centro de costos.' });
                setLoadDialog(null);
                fetchData();
            } else {
                throw new Error(res?.message || 'Error');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error?.response?.data?.message || error.message || 'No se pudo cargar al centro de costos.',
                variant: 'destructive',
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await deleteSettlement(id);
            if (res?.state) {
                toast({ title: 'Éxito', description: 'Liquidación eliminada.' });
                fetchData();
            }
        } catch (error) {
            toast({ title: 'Error', description: 'No se pudo eliminar la liquidación.', variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Liquidaciones Guardadas</CardTitle>
                <CardDescription>
                    {employeeId
                        ? 'Liquidaciones del empleado seleccionado.'
                        : 'Selecciona un empleado para ver sus liquidaciones.'}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Empleado</TableHead>
                                <TableHead>Período</TableHead>
                                <TableHead>Centro</TableHead>
                                <TableHead className="text-right">Total a Pagar</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Centro de Costos</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center h-20 text-muted-foreground">Cargando...</TableCell>
                                </TableRow>
                            ) : settlements.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center h-20 text-muted-foreground">
                                        No hay liquidaciones guardadas.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                settlements.map((s) => (
                                    <TableRow key={s._id}>
                                        <TableCell className="font-medium">{s.empleado?.nombre}</TableCell>
                                        <TableCell>
                                            {fmtDate(s.periodo?.fechaInicio)} — {fmtDate(s.periodo?.fechaFin)}
                                        </TableCell>
                                        <TableCell>{s.lavanderia?.nombre || '—'}</TableCell>
                                        <TableCell className="text-right font-bold text-blue-600">
                                            {formatCurrency(s.valores?.totalNeto ?? s.valores?.totalAPagar)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{s.estado}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            {s.cargadoACentroCostos ? (
                                                <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Cargado</Badge>
                                            ) : (
                                                <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pendiente</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" title="Ver colilla" onClick={() => setColillaSettlement(s)}>
                                                <FileText className="h-4 w-4 text-gray-600" />
                                            </Button>
                                            {!s.cargadoACentroCostos && (
                                                <Button variant="ghost" size="sm" title="Cargar a Centro de Costos" onClick={() => openLoadDialog(s)}>
                                                    <Building2 className="h-4 w-4 text-blue-600" />
                                                </Button>
                                            )}
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(s._id)}>
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>

            {/* Diálogo cargar a centro de costos */}
            <Dialog open={!!loadDialog} onOpenChange={(open) => !open && setLoadDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cargar a Centro de Costos</DialogTitle>
                        <DialogDescription>
                            Se creará un costo de categoría "Nómina" por {formatCurrency(loadDialog?.valores?.totalNeto ?? loadDialog?.valores?.totalAPagar)} asociado al centro seleccionado.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="text-sm font-medium mb-2 block">Centro de lavado</label>
                        <Select value={selectedCentro} onValueChange={setSelectedCentro}>
                            <SelectTrigger>
                                <SelectValue placeholder="Selecciona un centro" />
                            </SelectTrigger>
                            <SelectContent>
                                {laundries.map((l) => (
                                    <SelectItem key={l._id} value={l._id}>{l.nombre}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-2">
                            Por defecto se usa la lavandería del empleado; puedes elegir otra.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLoadDialog(null)}>Cancelar</Button>
                        <Button onClick={handleLoadToCostCenter} disabled={processing}>
                            {processing ? 'Cargando...' : 'Cargar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Vista / impresión de colilla */}
            <ColillaModal settlement={colillaSettlement} onClose={() => setColillaSettlement(null)} />
        </Card>
    );
};

export default SavedSettlements;
